"use client";

import { X } from "lucide-react";

import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { DateRangePicker, DateRangePickerRef } from "@/pages/tax/access-request/components/date-range-picker";
import { useGetRegionalsCashBalanceQuery } from "@/services/cash-balance";
import capitalizeFirstLetter from "@/utils/capitalize-first-letter";
import { Input } from "components/ui/input";
import { Label } from "components/ui/label";
import { debounce } from "lodash";
import React, { useCallback, useEffect, useState } from "react";
import { DateRange } from "react-day-picker";
import { useFilterActions } from "../ducks/hooks/action";
import { useCashBalanceState } from "../ducks/hooks/state";
import { SelectInput } from "components/Select";
import { getSelectClasses } from "@/utils/filter-styles";
import { formatOptionLabel } from "@/utils/format-option-label";
import { useGetStoresOptionsQuery } from "@/services/store/select-options";
import { MultiValueTag } from "@/components/ui/MultiValueTag";
import { formatFilterValue } from "@/utils/formatters";

interface DataTableToolbarProps {
  actions?: React.ReactNode;
}

export function DataTableToolbar({ actions }: DataTableToolbarProps) {
  const { updateFilter, clearFilters } = useFilterActions();
  const filterState = useCashBalanceState();
  const [searchValue, setSearchValue] = useState(
    filterState.filters.search || ""
  );
  const [regionalValue, setRegionalValue] = useState("");
  const [storeValue, setStoreValue] = useState<any[]>([]);
  const [dateRange, setDateRange] = React.useState<DateRange | undefined>(undefined);
  const { data: storeOptions = [] } = useGetStoresOptionsQuery();

  const debouncedSearchUpdate = useCallback(
    debounce((value: string) => {
      updateFilter("search", value);
    }, 500),
    []
  );

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchValue(event.target.value);
    debouncedSearchUpdate(event.target.value);
  };

  useEffect(() => {
    setSearchValue(filterState.filters.search || "");
  }, [filterState.filters.search]);

  const handleClearFilters = () => {
    clearFilters();
    setRegionalValue("");
    setStoreValue([]);
    setDateRange(undefined);
    dateRangePickerRef.current?.clear();
    updateFilter("regional_id", undefined);
    updateFilter("store_code", undefined);
    updateFilter("created_at_start", undefined);
    updateFilter("created_at_end", undefined);
  };

  const handleStoreChange = (options: any) => {
    const selectedOptions = Array.isArray(options) ? options : options ? [options] : [];
    setStoreValue(selectedOptions);
    const values = selectedOptions.map((opt: any) => opt.value);
    updateFilter("store_code", formatFilterValue(values) || undefined);
  };

  const { filters } = useCashBalanceState();
  const { data } = useGetRegionalsCashBalanceQuery();

  useEffect(() => {
    if (!filters.regional_id) {
      updateFilter("regional_id", undefined);
    }
  }, [filters.regional_id, updateFilter]);

  const handleSelectRegionalChange = (value: string) => {
    setRegionalValue(value);
    if (value === "all") {
      updateFilter("regional_id", undefined);
    } else {
      updateFilter("regional_id", Number(value));
    }
  };

  const regionalOptions =
    data?.data
      ?.filter((regional) => regional?.REGIONAL)
      ?.map((regional) => ({
        value: regional.REGIONAL.toString(),
        label: regional?.EMAIL.replace("@grupoavenida.com.br", "")
          .split(".")
          .map(word => capitalizeFirstLetter(word))
          .join(" "),
      })) || [];


  const handleDateRangeChange = useCallback(
    (startDate: string | undefined, endDate: string | undefined) => {
      setDateRange({
        from: startDate ? new Date(startDate) : undefined,
        to: endDate ? new Date(endDate) : undefined,
      });
      updateFilter("created_at_start", startDate || "");
      updateFilter("created_at_end", endDate || "");
    },
    [updateFilter]
  );

  const dateRangePickerRef = React.useRef<DateRangePickerRef>(null);

  return (
    <div className="flex flex-col flex-wrap md:flex-row md:items-center md:justify-between space-y-4 md:space-y-0">
      <div className="flex flex-col md:flex-row flex-wrap items-center space-x-4 space-y-4 md:space-y-0">
        <div className="flex flex-col">
          <Label htmlFor="search-input" className="mb-2">
            Responsável
          </Label>
          <Input
            id="search-input"
            placeholder="Filtrar por responsável"
            value={searchValue}
            onChange={handleSearchChange}
            className="h-8 w-[150px] lg:w-[200px]"
          />
        </div>

        <div className="flex flex-col">
          <label htmlFor="regional" className="text-sm font-medium text-gray-700">
            Regional
          </label>
          <Select
            value={regionalValue}
            onValueChange={handleSelectRegionalChange}
          >
            <SelectTrigger
              id="regional"
              className="h-8 w-[200px]"
            >
              <SelectValue placeholder="Selecione um Regional" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todos</SelectItem>
              {regionalOptions?.map((regional) => (
                <SelectItem key={regional.value} value={regional.value}>
                  {regional.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div className="flex flex-col">
          <label htmlFor="store_code" className="text-sm font-medium text-gray-700">
            Loja
          </label>
          <SelectInput
            options={storeOptions || []}
            placeholder="Selecionar loja"
            value={storeValue}
            onChange={handleStoreChange}
            className={`${getSelectClasses(storeValue.length > 0)} h-8 min-w-[180px]`}
            classNamePrefix="an-simple-select"
            isMulti
            isClearable
            closeMenuOnSelect={false}
            hideSelectedOptions={false}
            components={{ MultiValue: MultiValueTag }}
            formatOptionLabel={formatOptionLabel}
          />
        </div>
        <div className="flex flex-col">
          <label htmlFor="date_range" className="text-sm font-medium text-gray-700">
            Data
          </label>
          <DateRangePicker
            ref={dateRangePickerRef}
            onDateChange={handleDateRangeChange}
            initialDateRange={dateRange}
          />
        </div>
        {Object.keys(filterState.filters).length > 0 && (
          <Button
            variant="outline"
            onClick={handleClearFilters}
            className="h-8"
          >
            <X className="w-3 h-3 text-gray-500 group-hover:text-gray-700 transition-colors" />
            <span className="font-medium text-sm">Resetar Filtros</span>
          </Button>
        )}
      </div>
      {actions && <div className="flex items-center">{actions}</div>}
    </div>
  );
}
